import { useRouter } from 'next/router';
import styles from './BroadcasterPlayer.module.scss';
import ShareIcon from '@/icons/share.svg';
import { ToastStatus, useToast } from '@/dls/Toast/Toast';
import useTranslation from 'next-translate/useTranslation';
import useSWRImmutable from 'swr/immutable';
import { getVideos, getVideosById } from '@/api';
import { timeAgo, formatDuration } from '@/utils/common';

const BroadcasterPlayerPage = () => {
  const router = useRouter();
  const { id } = router.query;
  const toast = useToast();
  const { t } = useTranslation();
  let {
    data: getVideosData,
    // error: videosError,
  } = useSWRImmutable('videos', async () => {
    const res = await getVideos();
    return res.videos;
  });

  let {
    data: videoData,
    error: videoError,
  } = useSWRImmutable(
    id ? `videos/${id}` : null,
    async () => {
      const res = await getVideosById(id);
      return res;
    }
  );

  const handleShareClick = () => {
    if (typeof window !== 'undefined') {
      const url = window.location.href;
      navigator.clipboard.writeText(url).then(() => {
        toast(t('common:shared'), { status: ToastStatus.Success });
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    }
  };

  const handleVideoClick = (video: any) => {

    router.push(
      {
        pathname: router.pathname, // stays on the same page
        query: { id: video.id },
      },
      undefined,
      { shallow: false } // ensures SWR runs again
    );
  };

  const handleVideoEnded = () => {
    // Get the next video from the list
    if (getVideosData && videoData) {
      const nextVideo = getVideosData.find((v: any, index: number) => {
        const currentIndex = getVideosData.findIndex((vid: any) => vid.id === videoData.id);
        return index === currentIndex + 1;
      });

      if (nextVideo) {
        handleVideoClick(nextVideo);
      }
    }
  };


  // Main video loading
  if (!videoData && !videoError) {
    return <div className={styles.loading}>Loading video...</div>;
  }

  if (videoError) {
    return <div className={styles.loading}>Failed to load video</div>;
  }

  if (!videoData) {
    return <div className={styles.loading}>No video found</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        <div className={styles.videoWrapper}>
          <video
            key={videoData.id}
            className={styles.video}
            controls
            controlsList="nodownload" // Prevents downloading
            // poster={videoData.thumbnail}
            autoPlay
            onEnded={handleVideoEnded}
          >
            <source src={videoData.url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "1rem" }}>
            <h2 className={styles.title}>{videoData.title}</h2>
            <button className={styles.shareButton} onClick={handleShareClick}>
              <ShareIcon />
              <span>Share</span>
            </button>
          </div>
          <h4 key={videoData.viewCount}>{videoData.viewCount} Views • {timeAgo(videoData.createdAt)}</h4>
          {/* <h4>{videoData.viewCount}&nbsp;Views • 1 hour ago</h4> */}
        </div>
      </div>

      {/* Sidebar */}
      <div className={styles.sidebar}>
        {/* {!getVideosData && !videosError && (
          <div className={styles.loading}>Loading suggestions...</div>
        )}
        {videosError && (
          <div className={styles.loading}>Failed to load suggestions</div>
        )} */}

        {getVideosData?.filter((video) => video.id != videoData.id)?.map((video) => (
          <div
            key={video.id}
            className={styles.videoSuggestions}
            onClick={() => handleVideoClick(video)}
          >
            <div style={{ display: "flex" }}>
              <div className={styles.thumbnailWrapper}>
                <video className={styles.videoThumb} poster={video.thumbnail}>
                  <source src={video.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <span className={styles.timestamp}>{formatDuration(video.duration)}</span>
              </div>
              <div className={styles.videoDetails}>
                <h3 className={styles.videoTitle}>{video.title}</h3>
                <p className={styles.metaInfo} key={video.viewsCount}>
                  {video.viewsCount} &nbsp;Views •  {timeAgo(videoData.createdAt)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BroadcasterPlayerPage;
